import React, { Component } from 'react';
import Inherit from '../../core/inherit';
import DashboardController from './dashboard.controller';
import withNavigate from '../../core/withNavigate';
import { Progress, Tag, Button } from 'antd';
import { Users, Store, Building2, ClipboardCheck, Trophy, TrendingUp, Clock, TriangleAlert, RotateCw, UsersRound, Activity } from 'lucide-react';
import dayjs from 'dayjs';

import { Layout, DashboardSkeleton, PageHeader, StatCard, SectionCard, EmptyState, MetricRow } from '../common';
import { formatNumber } from '../../utils/format';
import { t } from '../../company/labels';
import { isFeatureEnabled } from '../../company/session';
import { FEATURES } from '../../company/features';

// Team rows; a row with `feature` only shows for companies that have it.
const TEAM_ROWS = [
    { key: 'gerentes', label: 'Gerentes', tone: 'bg-primary-500' },
    { key: 'asesores', label: 'Asesores', tone: 'bg-success-solid' },
    { key: 'promotores', label: t('promotores'), tone: 'bg-warning-solid' },
    { key: 'auditors', label: 'Auditores', tone: 'bg-info-solid', feature: FEATURES.AUDITOR }
];
const TEAM = TEAM_ROWS.filter((row) => !row.feature || isFeatureEnabled(row.feature));

/** One bar of the team distribution. */
const TeamBar = ({ label, value, total, tone }) => {
    const pct = total ? Math.round((value / total) * 100) : 0;
    return (
        <div>
            <div className="flex items-baseline justify-between mb-1.5">
                <span className="text-sm text-surface-600">{label}</span>
                <span className="text-sm font-semibold text-surface-900 tabular-nums">
                    {formatNumber(value)}
                    <span className="ml-1.5 text-xs font-normal text-surface-400">{pct}%</span>
                </span>
            </div>
            <div className="h-1.5 rounded-full bg-surface-100 overflow-hidden">
                <div className={`h-full rounded-full ${tone}`} style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
};

/**
 * Dashboard. Base page: a company overrides a seam (`renderOverview`, `renderTeam`, …) in a
 * subclass registered as `pages.Dashboard` in `companies/<id>/index.js`.
 */
export class ShelfDashboard extends Inherit(DashboardController, Component) {
    state = {
        analytics: {
            clients: { total: 0, active: 0, inactive: 0, moderno: 0, tradicional: 0 },
            stores: { registered: 0, active: 0, moderno: 0, tradicional: 0, uniqueAudited: 0, coverage: 0 },
            users: { total: 0, promotores: 0, asesores: 0, gerentes: 0, auditors: 0 },
            audits: { total: 0, thisWeek: 0, today: 0, avgTime: 0 },
            recentActivity: []
        },
        recentActivity: [],
        loading: true,
        error: null
    };

    renderLoading() {
        return (
            <Layout current="dashboard" user={this.user}>
                <DashboardSkeleton />
            </Layout>
        );
    }

    renderError(error) {
        return (
            <Layout current="dashboard" user={this.user}>
                <div className="shelf-card mt-10 max-w-md mx-auto">
                    <EmptyState
                        tone="danger"
                        icon={<TriangleAlert />}
                        title="No se pudo cargar el dashboard"
                        description={error}
                        action={
                            <Button type="primary" icon={<RotateCw />} onClick={() => this.retry()}>
                                Reintentar
                            </Button>
                        }
                    />
                </div>
            </Layout>
        );
    }

    renderHeader() {
        return <PageHeader title="Dashboard" description={`Resumen general de tu operación · ${dayjs().format('dddd, D [de] MMMM YYYY')}`} />;
    }

    /** KPI rows: clients & stores, then users, audits and coverage. */
    renderOverview(analytics) {
        const coverageGood = analytics.stores.coverage >= 80;
        return (
            <>
                {/* Clients & stores */}
                <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 mb-4">
                    <StatCard label="Total Clientes" value={formatNumber(analytics.clients.total)} icon={<Building2 />} tone="brand" hint="Clientes registrados" />
                    <StatCard label="Clientes Moderno" value={formatNumber(analytics.clients.moderno)} icon={<Store />} tone="info" hint="Canal moderno" />
                    <StatCard label="Clientes Tradicional" value={formatNumber(analytics.clients.tradicional)} icon={<Store />} tone="warning" hint="Canal tradicional" />
                    <StatCard label="Pisos de Venta" value={formatNumber(analytics.stores.active)} icon={<Store />} tone="success" hint="Activos actualmente" />
                </div>

                {/* Users, audits, coverage */}
                <div className="grid gap-4 grid-cols-1 lg:grid-cols-3 mb-4">
                    <StatCard
                        label="Usuarios del Sistema"
                        value={formatNumber(analytics.users.total)}
                        icon={<Users />}
                        tone="violet"
                        footer={
                            <>
                                <MetricRow label={t('promotores')} value={formatNumber(analytics.users.promotores)} />
                                <MetricRow label="Asesores" value={formatNumber(analytics.users.asesores)} />
                            </>
                        }
                    />
                    <StatCard
                        label="Auditorías del Mes"
                        value={formatNumber(analytics.audits.total)}
                        icon={<ClipboardCheck />}
                        tone="info"
                        footer={
                            <>
                                <MetricRow
                                    label="Esta semana"
                                    value={
                                        <span className="inline-flex items-center gap-1 text-success-fg">
                                            {formatNumber(analytics.audits.thisWeek)}
                                            <TrendingUp className="text-xs" />
                                        </span>
                                    }
                                />
                                <MetricRow label="Hoy" value={formatNumber(analytics.audits.today)} />
                            </>
                        }
                    />
                    <StatCard
                        label="Cobertura"
                        help="Pisos de venta activos con al menos una auditoría este mes."
                        value={`${analytics.stores.coverage}%`}
                        valueTone={coverageGood ? 'success' : 'warning'}
                        icon={<Trophy />}
                        tone={coverageGood ? 'success' : 'warning'}
                        footer={
                            <>
                                <MetricRow
                                    label="Auditados"
                                    value={`${formatNumber(analytics.stores.uniqueAudited)} / ${formatNumber(analytics.stores.active)}`}
                                />
                                <Progress
                                    percent={analytics.stores.coverage}
                                    showInfo={false}
                                    strokeColor={coverageGood ? '#12a06e' : '#e08a12'}
                                    size={['100%', 6]}
                                    className="!mb-0 !mt-1"
                                />
                            </>
                        }
                    />
                </div>
            </>
        );
    }

    /** Team distribution. */
    renderTeam(analytics) {
        const teamTotal = TEAM.reduce((sum, row) => sum + (analytics.users[row.key] || 0), 0);
        return (
            <SectionCard title="Equipo de Trabajo" icon={<UsersRound />} className="lg:col-span-2">
                <div className="flex items-baseline gap-2 mb-5">
                    <span className="text-[28px] font-semibold tracking-tight tabular-nums text-surface-900">{formatNumber(teamTotal)}</span>
                    <span className="text-sm text-surface-500">personas en campo y gestión</span>
                </div>
                <div className="space-y-4">
                    {TEAM.map((row) => (
                        <TeamBar key={row.key} label={row.label} value={analytics.users[row.key] || 0} total={teamTotal} tone={row.tone} />
                    ))}
                </div>
            </SectionCard>
        );
    }

    /** Latest store checks. */
    renderRecentActivity(recentActivity) {
        return (
            <SectionCard
                title="Actividad Reciente"
                icon={<Activity />}
                className="lg:col-span-3"
                flush
                extra={<Tag className="!m-0">{recentActivity?.length || 0} auditorías</Tag>}>
                {recentActivity && recentActivity.length > 0 ? (
                    <ul className="m-0 p-0 list-none divide-y divide-surface-100">
                        {recentActivity.map((audit, index) => (
                            <li key={audit._id || index} className="flex items-center gap-3 px-5 py-3 hover:bg-surface-25 transition-colors">
                                <span
                                    className={`h-2 w-2 rounded-full shrink-0 ${
                                        audit.status === 'completed' ? 'bg-success-solid' : 'bg-primary-500'
                                    }`}
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-surface-900 truncate">{audit.storeId?.name || 'Tienda desconocida'}</div>
                                    <div className="text-xs text-surface-500 truncate">{audit.userName || 'Usuario desconocido'}</div>
                                </div>
                                <div className="flex flex-col items-end gap-1 shrink-0">
                                    <Tag color={audit.status === 'completed' ? 'success' : 'processing'} className="!m-0">
                                        {audit.status === 'completed' ? 'Completado' : 'En proceso'}
                                    </Tag>
                                    <span className="flex items-center gap-1 text-xs text-surface-400 tabular-nums">
                                        <Clock />
                                        {dayjs(audit.createdAt).format('DD/MM/YYYY HH:mm')}
                                    </span>
                                </div>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <EmptyState icon={<ClipboardCheck />} title="No hay actividad reciente" description="Las auditorías aparecerán aquí" />
                )}
            </SectionCard>
        );
    }

    renderContent(analytics, recentActivity) {
        return (
            <Layout current="dashboard" user={this.user}>
                {this.renderHeader()}

                {this.renderOverview(analytics)}

                <div className="grid gap-4 grid-cols-1 lg:grid-cols-5">
                    {this.renderTeam(analytics)}

                    {this.renderRecentActivity(recentActivity)}
                </div>
            </Layout>
        );
    }

    render() {
        const { analytics, recentActivity, loading, error } = this.state;
        if (loading) return this.renderLoading();
        if (error) return this.renderError(error);
        return this.renderContent(analytics, recentActivity);
    }
}

export default withNavigate(ShelfDashboard);
